import {
  dayMonth,
  materialLink,
  shortName,
  simOfUrl,
  timeOf,
  useAuth,
  useCatchUpMark,
  useCatchUpPlan,
  useLessonPack,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { FileText, FlaskConical, type LucideIcon, Link2, Presentation, Zap } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_SOFT,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import { ArrowUpRightIcon, BackpackIcon, CheckIcon } from '../design/icons';
import { plain } from '../student/home/model';

/*
 * «Пакет уроку» in «Форми 2» (28.10): the whole lesson for whoever missed it. The subject on the paper
 * for a title, the day, the class and the teacher under it; the topic on a soft card of the subject's
 * gradient with its picture big and quiet behind (and the «I caught up» for the student who missed it);
 * then white blocks — what to bring, the test, the homework, the materials, the boards — each thing a
 * soft row of the paper with a small icon tile and a ↗ where it leads somewhere.
 */

/** A row of a block: a small gradient tile with a line icon, the words, the note on the right. */
const row =
  'group flex w-full items-center gap-3 rounded-[18px] bg-[#F5F3EF] px-3 py-2.5 text-left transition-colors hover:bg-[#EFECE5]';

function Tile({ icon: Icon, tone }: { icon: LucideIcon; tone: Grad }) {
  return (
    <span
      aria-hidden
      className="flex size-10 shrink-0 items-center justify-center rounded-[12px]"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <Icon size={19} strokeWidth={1.8} />
    </span>
  );
}

/** The note at the right of a row and the ↗ when the row leads somewhere. */
function Tail({ note, arrow = true }: { note?: ReactNode; arrow?: boolean }) {
  return (
    <>
      {note && (
        <span className="shrink-0 text-[12.5px]" style={{ color: INK_SOFT }}>
          {note}
        </span>
      )}
      {arrow && (
        <ArrowUpRightIcon
          size={14}
          weight="bold"
          aria-hidden
          color={INK}
          className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      )}
    </>
  );
}

/** A file opens in a new tab: the link of a private file is made on the click. */
function MaterialRow({
  m,
}: {
  m: { title: string; url: string | null; storagePath: string | null; mimeType: string | null };
}) {
  const { t } = useTranslation();
  const { supabase } = useAuth();
  const [error, setError] = useState(false);
  const sim = simOfUrl(m.url);
  if (sim) {
    return (
      <Link to="/lab/sim/$slug" params={{ slug: sim.slug }} className={row}>
        <Tile icon={FlaskConical} tone="aqua" />
        <span className="min-w-0 flex-1 text-[15px]" style={{ color: INK }}>
          {m.title}
        </span>
        <Tail note={t('lessonPack.sim')} />
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={row}
      onClick={() => {
        setError(false);
        // a new tab first: browsers block windows opened after an await
        const tab = window.open('', '_blank');
        if (tab) tab.opener = null;
        materialLink(supabase, m).then(
          (url) => {
            if (tab) tab.location.href = url;
            else window.location.href = url;
          },
          () => {
            tab?.close();
            setError(true);
          },
        );
      }}
    >
      <Tile icon={m.url ? Link2 : FileText} tone={m.url ? 'sky' : 'sand'} />
      <span className="min-w-0 flex-1 text-[15px]" style={{ color: INK }}>
        {m.title}
      </span>
      <Tail
        note={
          error ? (
            <span style={{ color: GRAD.coral.ink }}>{t('common.error')}</span>
          ) : m.url ? (
            t('lessonPack.link')
          ) : (
            t('lessonPack.file')
          )
        }
      />
    </button>
  );
}

/** «Пакет уроку»: the whole lesson for whoever missed it. */
export function LessonPackPage({ lessonId }: { lessonId: string }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const pack = useLessonPack(lessonId);
  // the student who missed it ticks it off here too («Догнати пропущене»)
  const plan = useCatchUpPlan(child?.memberId ?? '');
  const mark = useCatchUpMark();
  if (pack.isPending) return <Spinner />;
  const p = pack.data;
  if (!p) {
    return (
      <div className="flex flex-col gap-4">
        <PageHead title={t('lessonPack.title')} />
        <Block>
          <p className="py-6 text-center text-[15px]" style={{ color: INK_SOFT }}>
            {t('lessonPack.notFound')}
          </p>
        </Block>
      </div>
    );
  }
  const l = p.lesson;
  const teacher = l.substitute ?? l.teachers[0];
  const missed = plan.data?.find((i) => i.lessonId === lessonId);
  const nothing =
    p.materials.length === 0 && p.boards.length === 0 && p.homework.length === 0 && !l.bring;
  const look = subjectLook(l.subject);
  const blocks = [l.bring, p.test, p.homework.length, p.materials.length, p.boards.length].filter(
    Boolean,
  ).length;
  const tone = SUBJECT_GRAD[look.tone];
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={l.subject}
        note={t('lessonPack.title')}
        sub={[
          `${dayMonth(l.date)}, ${timeOf(l.startsAt)}–${timeOf(l.endsAt)}`,
          l.classLabel,
          teacher && shortName(teacher),
        ]
          .filter(Boolean)
          .join(' · ')}
      />

      {/* the topic on the subject's colour, its picture big and quiet behind */}
      <section
        className="relative isolate overflow-hidden rounded-[28px] p-6 md:p-7"
        style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
      >
        <SubjectArt
          icon={look.icon}
          tone={tone}
          size={200}
          ghost
          className="pointer-events-none absolute -top-8 -right-6 -z-10"
        />
        <p className="text-[13px] font-medium">{t('studentHome.day.topic')}</p>
        <p
          className="mt-1 max-w-2xl md:pr-24 font-display text-[28px] leading-[34px] font-light tracking-[-0.03em] md:text-[34px] md:leading-[40px]"
          style={{ color: INK }}
        >
          {l.topic ?? t('lessonPack.noTopic')}
        </p>
        {missed && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <p
              className="rounded-[10px] bg-white/75 px-3 py-2 text-[14px] leading-5"
              style={{ color: INK }}
            >
              {ctx.kind === 'student' ? t('lessonPack.missed') : t('lessonPack.missedFamily')}
            </p>
            {ctx.kind === 'student' && child && (
              <button
                type="button"
                onClick={() =>
                  mark.mutate({
                    schoolId: ctx.schoolId,
                    studentId: child.memberId,
                    lessonId,
                    done: !missed.done,
                  })
                }
                className={pillClass(missed.done ? 'light' : 'dark')}
              >
                {missed.done ? t('lessonPack.done') : t('lessonPack.markDone')}
                <PillIcon icon={CheckIcon} />
              </button>
            )}
          </div>
        )}
      </section>

      {/* two columns when there are two or more blocks; an odd last one takes the whole row */}
      <div
        className={`grid grid-cols-1 items-start gap-4 ${blocks >= 2 ? 'lg:grid-cols-2 lg:[&>*:last-child:nth-child(odd)]:col-span-2' : ''}`}
      >
        {l.bring && (
          <Block>
            <Head title={t('lessonPack.bring')}>
              <span
                className="flex size-9 items-center justify-center rounded-full"
                style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
              >
                <BackpackIcon size={17} weight="fill" aria-hidden />
              </span>
            </Head>
            <p className="text-[15.5px] leading-6" style={{ color: INK }}>
              {plain(l.bring)}
            </p>
          </Block>
        )}
        {p.test && (
          <Block>
            <Head title={t('lessonPack.test')} />
            <Link to="/quizzes" className={row}>
              <Tile icon={Zap} tone="coral" />
              <span className="min-w-0 flex-1 text-[15px]" style={{ color: INK }}>
                {p.test.title}
              </span>
              <Tail note={t('lessonPack.due', { date: dayMonth(p.test.dueOn) })} />
            </Link>
          </Block>
        )}
        {p.homework.length > 0 && (
          <Block>
            <Head title={t('lessonPack.homework')} count={p.homework.length} />
            <div className="flex flex-col gap-1.5">
              {p.homework.map((h) => (
                <Link
                  key={h.id}
                  to="/homework"
                  className="group relative isolate flex flex-col gap-1 overflow-hidden rounded-[20px] px-4 py-3 transition-[filter] hover:brightness-[0.98]"
                  style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
                >
                  <SubjectArt
                    icon={look.icon}
                    tone={tone}
                    size={86}
                    ghost
                    className="pointer-events-none absolute -top-3 right-10 -z-10"
                  />
                  <span className="flex w-full items-start gap-3">
                    <span
                      className="min-w-0 flex-1 text-[16px] leading-[22px] font-medium"
                      style={{ color: INK }}
                    >
                      {h.title}
                    </span>
                    <span className="shrink-0 pt-0.5 text-[12.5px]">
                      {t('lessonPack.due', { date: dayMonth(h.dueOn) })}
                    </span>
                    <ArrowUpRightIcon
                      size={14}
                      weight="bold"
                      aria-hidden
                      color={INK}
                      className="mt-1 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                  {h.body && (
                    <span className="text-[14px] leading-5" style={{ color: INK_SOFT }}>
                      {h.body}
                    </span>
                  )}
                  {h.criteria && (
                    <span className="text-[12.5px]" style={{ color: INK_SOFT }}>
                      {t('lessonPack.criteria')}: {h.criteria}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </Block>
        )}
        {p.materials.length > 0 && (
          <Block>
            <Head title={t('lessonPack.materials')} count={p.materials.length} />
            <div className="flex flex-col gap-1.5">
              {p.materials.map((m) => (
                <MaterialRow key={m.id} m={m} />
              ))}
            </div>
          </Block>
        )}
        {p.boards.length > 0 && (
          <Block>
            <Head title={t('lessonPack.boards')} />
            <div className="flex flex-col gap-1.5">
              {p.boards.map((b) => (
                <Link key={b.id} to="/board/$boardId" params={{ boardId: b.id }} className={row}>
                  <Tile icon={Presentation} tone="lavender" />
                  <span className="min-w-0 flex-1 text-[15px]" style={{ color: INK }}>
                    {b.title}
                  </span>
                  <Tail />
                </Link>
              ))}
            </div>
          </Block>
        )}
        {nothing && (
          <Block className="items-center py-10 text-center">
            <TaskArt kind="catchUp" tone="lavender" size={88} />
            <p
              className="mt-3 font-display text-[22px] leading-7 font-light"
              style={{ color: INK }}
            >
              {t('lessonPack.empty')}
            </p>
          </Block>
        )}
      </div>
    </div>
  );
}
